import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageSquare,
  Settings,
  BarChart,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Instagram,
  Zap,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  // Agent Config State
  const [agentConfig, setAgentConfig] = useState({
    enabled: false,
    business_hours_only: false,
    start_hour: 9,
    end_hour: 18,
    default_response: ''
  });

  // Stats State
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [instagramId, setInstagramId] = useState('');

  // Check if already authenticated or just completed OAuth
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get('auth');
    
    if (authSuccess === 'success') {
      // User just authenticated via Instagram OAuth
      setAuthenticated(true);
      fetchAgentConfig();
      
      // Clean URL
      window.history.replaceState({}, document.title, '/admin');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/verify`,
        {
          auth: {
            username: username,
            password: password
          }
        }
      );

      if (response.data.authenticated) {
        setAuthenticated(true);
        fetchAgentConfig();
      }
    } catch (error) {
      setAuthError('Falsche Zugangsdaten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/instagram/agent/config`);
      setAgentConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const updateAgentConfig = async (updates) => {
    try {
      await axios.post(`${API_BASE_URL}/api/instagram/agent/config`, updates);
      setAgentConfig({ ...agentConfig, ...updates });
    } catch (error) {
      console.error('Failed to update config:', error);
    }
  };

  const fetchStats = async () => {
    if (!instagramId) return;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/instagram/stats`,
        { params: { user_instagram_id: instagramId } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchMessages = async () => {
    if (!instagramId) return;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/instagram/messages/${instagramId}`,
        { params: { limit: 50 } }
      );
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  useEffect(() => {
    if (authenticated && instagramId) {
      fetchStats();
      fetchMessages();
      
      // Refresh every 30 seconds
      const interval = setInterval(() => {
        fetchStats();
        fetchMessages();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [authenticated, instagramId]);

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-purple-600" />
            <CardTitle className="text-3xl font-bold">Admin Login</CardTitle>
            <CardDescription>Sicherer Zugang zum Instagram Agent Dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {authError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{authError}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="username">Benutzername</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Admin-Benutzername"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Admin-Passwort"
                  required
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
                <Instagram className="w-10 h-10 text-purple-600" />
                Instagram Agent Dashboard
              </h1>
              <p className="text-gray-600 mt-2">Verwalten Sie Ihren automatischen Antwort-Agenten</p>
            </div>
            <Badge variant={agentConfig.enabled ? "default" : "secondary"} className="text-lg px-4 py-2">
              {agentConfig.enabled ? (
                <><CheckCircle className="w-4 h-4 mr-2 inline" /> Aktiv</>
              ) : (
                <><XCircle className="w-4 h-4 mr-2 inline" /> Inaktiv</>
              )}
            </Badge>
          </div>
        </div>

        {/* Instagram ID Input */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Instagram Konto Konfiguration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Instagram User ID eingeben"
                value={instagramId}
                onChange={(e) => setInstagramId(e.target.value)}
              />
              <Button onClick={() => { fetchStats(); fetchMessages(); }}>
                Laden
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              💡 Ihre Instagram User ID finden Sie in der OAuth-Antwort nach der Autorisierung
            </p>
          </CardContent>
        </Card>

        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </TabsTrigger>
            <TabsTrigger value="messages">
              <MessageSquare className="w-4 h-4 mr-2" />
              Nachrichten
            </TabsTrigger>
            <TabsTrigger value="stats">
              <BarChart className="w-4 h-4 mr-2" />
              Statistiken
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Agent Aktivierung</CardTitle>
                <CardDescription>
                  Aktivieren oder deaktivieren Sie den automatischen Antwort-Agenten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="agent-enabled">Agent aktiviert</Label>
                    <p className="text-sm text-gray-500">
                      Automatische Antworten auf eingehende Nachrichten
                    </p>
                  </div>
                  <Switch
                    id="agent-enabled"
                    checked={agentConfig.enabled}
                    onCheckedChange={(checked) => updateAgentConfig({ enabled: checked })}
                  />
                </div>

                {agentConfig.enabled && (
                  <Alert>
                    <Zap className="h-4 w-4" />
                    <AlertDescription>
                      Der Agent antwortet jetzt automatisch auf eingehende Nachrichten!
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Geschäftszeiten</CardTitle>
                <CardDescription>
                  Beschränken Sie Agent-Antworten auf bestimmte Uhrzeiten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="business-hours">Nur während Geschäftszeiten</Label>
                    <p className="text-sm text-gray-500">
                      Agent antwortet nur in definierten Zeiten
                    </p>
                  </div>
                  <Switch
                    id="business-hours"
                    checked={agentConfig.business_hours_only}
                    onCheckedChange={(checked) =>
                      updateAgentConfig({ business_hours_only: checked })
                    }
                  />
                </div>

                {agentConfig.business_hours_only && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-hour">Start (Uhr)</Label>
                      <Input
                        id="start-hour"
                        type="number"
                        min="0"
                        max="23"
                        value={agentConfig.start_hour}
                        onChange={(e) =>
                          updateAgentConfig({ start_hour: parseInt(e.target.value) })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-hour">Ende (Uhr)</Label>
                      <Input
                        id="end-hour"
                        type="number"
                        min="0"
                        max="23"
                        value={agentConfig.end_hour}
                        onChange={(e) =>
                          updateAgentConfig({ end_hour: parseInt(e.target.value) })
                        }
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Agent Persönlichkeit</CardTitle>
                <CardDescription>
                  Definieren Sie, wie der Agent antworten soll
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="default-response">System-Prompt für Claude</Label>
                  <Textarea
                    id="default-response"
                    value={agentConfig.default_response}
                    onChange={(e) =>
                      setAgentConfig({ ...agentConfig, default_response: e.target.value })
                    }
                    placeholder="z.B. Du bist ein hilfreicher Assistent für freysmedia.de..."
                    rows={6}
                  />
                  <Button
                    onClick={() => updateAgentConfig({ default_response: agentConfig.default_response })}
                    className="mt-2"
                  >
                    Speichern
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Eingehende Nachrichten</CardTitle>
                <CardDescription>Letzte {messages.length} Nachrichten</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-12">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Noch keine Nachrichten vorhanden</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border ${
                            msg.processed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant={msg.processed ? "default" : "secondary"}>
                                {msg.processed ? 'Verarbeitet' : 'Ausstehend'}
                              </Badge>
                              <span className="text-xs text-gray-500">{msg.sender_id}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(msg.timestamp).toLocaleString('de-DE')}
                            </span>
                          </div>
                          
                          <p className="text-gray-800 mb-2">{msg.text}</p>
                          
                          {msg.ai_response && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-sm text-gray-600">
                                <strong>🤖 Agent-Antwort:</strong> {msg.ai_response}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Gesamt Nachrichten
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats?.total_messages || 0}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Verarbeitet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">
                    {stats?.processed_messages || 0}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Ausstehend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats?.unprocessed_messages || 0}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Konversationen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-purple-600">
                    {stats?.unique_conversations || 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Systemstatus</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">Agent Status</span>
                    {agentConfig.enabled ? (
                      <Badge variant="default">
                        <CheckCircle className="w-3 h-3 mr-1" /> Aktiv
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <XCircle className="w-3 h-3 mr-1" /> Inaktiv
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">Geschäftszeiten Modus</span>
                    <Badge variant={agentConfig.business_hours_only ? "default" : "secondary"}>
                      {agentConfig.business_hours_only ? 'Aktiviert' : 'Deaktiviert'}
                    </Badge>
                  </div>
                  
                  {agentConfig.business_hours_only && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium">Aktive Zeiten</span>
                      <Badge variant="outline">
                        <Clock className="w-3 h-3 mr-1" />
                        {agentConfig.start_hour}:00 - {agentConfig.end_hour}:00
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
